/* ------------------------------------------------------------------
   LinktreeImportCard.js  —  Link in Bio › Links › "Moving from Linktree?"
   (Part E, T33; BioExtras.dc.html). Paste a linktr.ee address, see what
   will come over, pick links, import:
     POST /api/bio/import/linktree/preview  { address }
     POST /api/bio/import/linktree          { address, urls, importAvatar }
   Duplicates and the Free plan's link cap are decided by the server; this
   card only shows them.
------------------------------------------------------------------ */
import React, { useState } from 'react';
import axios from 'axios';
import { BioIcon } from '../../bio/bioIcons';

const ax = axios.create({ withCredentials: true });

const errorText = (e, fallback) => e?.response?.data?.message || e?.response?.data?.error || fallback;

export default function LinktreeImportCard({ hasPage, onImported }) {
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(null);        // 'preview' | 'import' | null
  const [error, setError] = useState(null);
  const [found, setFound] = useState(null);      // { preview, limit, hasPage }
  const [picked, setPicked] = useState(() => new Set());
  const [useAvatar, setUseAvatar] = useState(true);

  async function loadPreview(e) {
    e.preventDefault();
    if (!address.trim()) { setError('Enter your Linktree address, like linktr.ee/yourname.'); return; }
    setBusy('preview'); setError(null); setFound(null);
    try {
      const { data } = await ax.post('/api/bio/import/linktree/preview', { address: address.trim() });
      setFound(data);
      setPicked(new Set(data.preview.links.filter(l => !l.duplicate).map(l => l.url)));
      setUseAvatar(!!data.preview.avatarUrl);
    } catch (err) {
      setError(errorText(err, "We couldn't read that Linktree page. Try again later, or add your links by hand."));
    } finally {
      setBusy(null);
    }
  }

  async function runImport() {
    const urls = found.preview.links.filter(l => picked.has(l.url)).map(l => l.url);
    setBusy('import'); setError(null);
    try {
      const { data } = await ax.post('/api/bio/import/linktree', { address: found.preview.url, urls, importAvatar: useAvatar && !!found.preview.avatarUrl });
      onImported(data);
      setFound(null); setAddress('');
    } catch (err) {
      setError(errorText(err, 'Import failed. Please try again.'));
    } finally {
      setBusy(null);
    }
  }

  const toggle = url => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(url)) next.delete(url); else next.add(url);
    return next;
  });

  const p = found?.preview;
  const remaining = found?.limit?.remaining;
  const count = p ? p.links.filter(l => picked.has(l.url)).length : 0;
  const canImport = hasPage && (count > 0 || (useAvatar && p?.avatarUrl));

  return (
    <div style={banner}>
      <div style={{ fontWeight: 700, color: '#1e3a8a' }}>Moving from Linktree? Bring everything over in one click.</div>
      <form onSubmit={loadPreview} style={{ display: 'flex', gap: 8 }}>
        <label htmlFor="linktree-address" style={srOnly}>Your Linktree address</label>
        <input id="linktree-address" value={address} onChange={e => setAddress(e.target.value)}
          placeholder="linktr.ee/yourname" autoComplete="off" spellCheck={false}
          style={{ flex: 1, minWidth: 0, padding: '9px 12px', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 14, background: '#fff', outline: 'none' }} />
        <button type="submit" disabled={!!busy} style={{ ...primary, opacity: busy === 'preview' ? 0.6 : 1 }}>
          {busy === 'preview' ? 'Reading…' : 'Import links'}
        </button>
      </form>
      {error && <div role="alert" style={{ fontSize: 13, color: '#dc2626' }}>{error}</div>}
      {!p && <div style={{ fontSize: 13, color: '#1e40af' }}>We copy your links, titles and profile photo. Then update your Instagram bio to your new link.</div>}

      {p && (
        <div aria-label="Linktree preview" role="region" style={panel}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={avatarBox}>
              {p.avatarUrl
                ? <img src={p.avatarUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : (p.title || p.username || '?').replace(/^@/, '').charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: '#111827' }}>{p.title}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>linktr.ee/{p.username} · {p.links.length} {p.links.length === 1 ? 'link' : 'links'}</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {p.links.map(l => (
              <label key={l.url} style={{ ...row, opacity: l.duplicate ? 0.6 : 1, cursor: l.duplicate ? 'default' : 'pointer' }}>
                <input type="checkbox" checked={picked.has(l.url)} disabled={l.duplicate} onChange={() => toggle(l.url)} />
                {l.icon && <BioIcon name={l.icon} size={14} />}
                <span style={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.title}</span>
                <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>{l.url}</span>
                {l.duplicate && <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>Already on your page</span>}
              </label>
            ))}
          </div>
          {p.lockedLinks > 0 && (
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {p.lockedLinks} locked {p.lockedLinks === 1 ? 'link isn’t' : 'links aren’t'} included (age-gated or members-only on Linktree).
            </div>
          )}

          {p.avatarUrl && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#111827', cursor: 'pointer' }}>
              <input type="checkbox" checked={useAvatar} onChange={e => setUseAvatar(e.target.checked)} />
              Use this profile photo
            </label>
          )}

          {remaining !== null && remaining !== undefined && count > remaining && (
            <div style={note}>
              The Free plan has up to {found.limit.max} links: {remaining === 0 ? 'no more fit' : `only the first ${remaining} will be added`}. Upgrade for unlimited.
            </div>
          )}
          {!hasPage && <div style={note}>Save your page first (Profile tab), then import your links.</div>}

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={runImport} disabled={!canImport || !!busy}
              style={{ ...primary, opacity: !canImport || busy ? 0.6 : 1 }}>
              {busy === 'import' ? 'Importing…' : count ? `Import ${count} ${count === 1 ? 'link' : 'links'}` : 'Import photo'}
            </button>
            <button type="button" onClick={() => { setFound(null); setError(null); }} disabled={!!busy} style={ghost}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const banner = { background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 };
const panel = { background: '#fff', border: '1px solid #dbeafe', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 };
const row = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, minWidth: 0 };
const avatarBox = { width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: '#111827', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 };
const note = { fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px' };
const primary = { padding: '9px 16px', border: 'none', background: '#2563eb', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const ghost = { padding: '9px 14px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const srOnly = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' };
